/**
 * Deal Analyzer — pure-function financial engine.
 *
 * GERMAN BANKING CONVENTION (universal — applied to all jurisdictions):
 *
 *   - Loan amount  = Total Project Cost − Equity   (NOT % of purchase price)
 *     Banks finance "everything the buyer cannot pay themselves", capped at
 *     the asset's lending value. This is how Annuitätendarlehen work in DE
 *     and is mathematically clean for Dubai / Cyprus too — the user just
 *     types the down payment they are willing to make.
 *
 *   - Mortgage type:
 *       • amortizing  → Annuitätendarlehen with **Anfangstilgung %** (no
 *                       term-in-years input). Year-1 interest = Loan × i,
 *                       year-1 principal = Loan × s. Total monthly payment
 *                       stays constant for the duration.
 *       • interest_only → Loan × i / 12 monthly. Principal stays untouched.
 *
 *   - Yields:
 *       • Net Yield (unlevered)   — Gesamtkapitalrendite
 *           = (Annual cold rent − landlord opex − vacancy reserve
 *              − maintenance reserve) / Total Project Cost
 *       • Equity Yield (levered) — Eigenkapitalrendite
 *           = (Annual cold rent − landlord opex − vacancy reserve
 *              − maintenance reserve − **interest** ) / Equity
 *
 *     CRITICAL: principal repayment (Tilgung) is NOT subtracted from the
 *     equity-yield numerator. It is wealth transfer from cash to equity in
 *     the asset, not an expense. This matches German banking conventions
 *     and is economically more correct than the US "cash-on-cash" approach
 *     that treats every euro of debt service as an expense.
 *
 *   - Vacancy reserve (Mietausfallwagnis) is applied to the gross rent
 *     (incl. service charges) at a configurable rate (2% German standard).
 *
 *   - Maintenance reserve (Instandhaltungsrücklage) is applied to the
 *     purchase price at a configurable rate (0.8% German standard,
 *     Peterscher Formel).
 *
 * Reference: "Kalkulation DE Immobilie.pdf" (uploaded by admin) — every
 * formula here matches that Excel calculator's logic to the cent.
 * The test harness (`dealMath.test.js`) reproduces the PDF example exactly.
 */

/** Safe number coercion — empty / null / NaN → 0. */
const n = (v) => {
  const x = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};

/**
 * Standard mortgage payment (PMT). Used only when the deal explicitly opts
 * into a US/CFA-style amortising loan with a known term — left in for
 * backwards-compatible legacy deals. New deals use Annuitätendarlehen.
 */
export function mortgagePayment({ principal, annualRatePct, termYears }) {
  const P = n(principal);
  const months = n(termYears) * 12;
  if (P <= 0 || months <= 0) return 0;
  const r = n(annualRatePct) / 100 / 12;
  if (r === 0) return P / months;
  const k = Math.pow(1 + r, months);
  return (P * r * k) / (k - 1);
}

/** Interest-only monthly payment. */
export function interestOnlyPayment({ principal, annualRatePct }) {
  return (n(principal) * (n(annualRatePct) / 100)) / 12;
}

/**
 * Full deal computation using German banking conventions for ALL deals
 * regardless of jurisdiction.
 *
 * Inputs (see `DEFAULT_DEAL` in DealAnalyzerPage.js for shape):
 *   purchase_price, closing_costs, renovation_costs   — same as before
 *   use_financing                                     — bool
 *   loan_amount                                       — Darlehenssumme € (primary input)
 *   equity                                            — Eigenkapital € (legacy fallback if loan_amount absent)
 *   interest_rate_pct                                 — Sollzins % p.a.
 *   tilgungsrate_pct                                  — Anfangstilgung % p.a.
 *   amortization                                      — "amortizing" | "interest_only"
 *   income_mode, monthly_rent, nightly_rate, occupancy_pct
 *   service_charges_annual, insurance_annual
 *   management_fee_pct                                — % of gross rent
 *   maintenance_reserve_pct                           — % of purchase price (Instandhaltung)
 *   vacancy_reserve_pct                               — % of gross rent (Mietausfallwagnis)
 *   utilities_annual, cleaning_monthly                — STR only
 */
export function computeDeal(input) {
  // ----- Acquisition (Anschaffungskosten) --------------------------------
  const purchasePrice = n(input.purchase_price);
  const closingCosts = n(input.closing_costs);
  const renovationCosts = n(input.renovation_costs);
  const totalProjectCost = purchasePrice + closingCosts + renovationCosts;

  // ----- Financing (German banking convention) ---------------------------
  // The user types the BANK LOAN amount (what the bank approved / what
  // appears on the Darlehensvertrag). Equity is the residual: whatever
  // total project cost the loan does not cover, the buyer brings as cash.
  // Legacy drafts that only stored `equity` (pre Feb 2026) still work via
  // the fallback below.
  const useFinancing = !!input.use_financing;
  let loanAmount = 0;
  let equity = totalProjectCost;
  if (useFinancing) {
    if (input.loan_amount !== undefined && input.loan_amount !== null && input.loan_amount !== "") {
      loanAmount = Math.min(Math.max(n(input.loan_amount), 0), totalProjectCost);
      equity = Math.max(totalProjectCost - loanAmount, 0);
    } else {
      // Legacy: derive loan from stored equity.
      equity = Math.max(n(input.equity), 0);
      loanAmount = Math.max(totalProjectCost - equity, 0);
    }
  }
  const downPayment = equity; // synonymous in this convention
  const cashInvested = equity;

  const interestRatePct = n(input.interest_rate_pct);
  const tilgungsRatePct = n(input.tilgungsrate_pct);
  const amortization = input.amortization || "amortizing";

  let monthlyInterest = 0;
  let monthlyPrincipal = 0;
  if (useFinancing && loanAmount > 0) {
    monthlyInterest = (loanAmount * (interestRatePct / 100)) / 12;
    if (amortization === "interest_only") {
      monthlyPrincipal = 0;
    } else {
      // Annuitätendarlehen — year-1 principal at the chosen Anfangstilgung rate.
      monthlyPrincipal = (loanAmount * (tilgungsRatePct / 100)) / 12;
    }
  }
  const monthlyDebtService = monthlyInterest + monthlyPrincipal;
  const annualDebtService = monthlyDebtService * 12;
  const annualInterest = monthlyInterest * 12;
  const annualPrincipal = monthlyPrincipal * 12;

  // ----- Income ----------------------------------------------------------
  const isStr = input.income_mode === "short_term";
  const monthlyRent = n(input.monthly_rent);
  const nightlyRate = n(input.nightly_rate);
  const occupancyPct = Math.min(Math.max(n(input.occupancy_pct), 0), 100);

  const annualGrossIncome = isStr
    ? nightlyRate * 365 * (occupancyPct / 100)
    : monthlyRent * 12;
  const occupiedNightsPerYear = isStr ? 365 * (occupancyPct / 100) : 0;

  // ----- Operating expenses (annual, landlord-borne) ---------------------
  const isGermany = (input.location || "").toLowerCase() === "germany";
  const insurance = n(input.insurance_annual);
  // Mietausfallwagnis — default 2% if unset (German industry standard).
  const vacancyPctRaw = input.vacancy_reserve_pct;
  const vacancyPct =
    vacancyPctRaw === undefined || vacancyPctRaw === null || vacancyPctRaw === ""
      ? 2
      : Math.min(Math.max(n(vacancyPctRaw), 0), 100);

  // Service charges:
  //   - Germany → replaced by the Hausgeld umlagefähig / nicht-umlagefähig split
  //     per Betriebskostenverordnung (BetrKV). Only nicht-umlagefähig hits the
  //     landlord; the umlagefähig portion is recovered from the tenant via
  //     Nebenkostenabrechnung and therefore tracked separately but NOT counted
  //     as a landlord cost.
  //   - All other countries → flat annual figure on `service_charges_annual`.
  const hausgeldUmlageMonthly      = isGermany ? n(input.hausgeld_umlage_monthly) : 0;
  const hausgeldNichtUmlageMonthly = isGermany ? n(input.hausgeld_nicht_umlage_monthly) : 0;
  const ruecklageMonthly           = isGermany ? n(input.instandhaltungsruecklage_monthly) : 0;
  const hausgeldUmlageAnnual       = hausgeldUmlageMonthly * 12;
  const hausgeldNichtUmlageAnnual  = hausgeldNichtUmlageMonthly * 12;
  const ruecklageAnnual            = ruecklageMonthly * 12;
  const serviceCharges = isGermany
    ? hausgeldNichtUmlageAnnual + ruecklageAnnual
    : n(input.service_charges_annual);

  // Management + Maintenance reserves — Deal Analyzer uses absolute annual
  // amounts (Feb 2026 simplification). Legacy `*_pct` fields are still
  // honoured for drafts saved before the switch.
  const managementFee = (input.management_fee_annual !== undefined
    && input.management_fee_annual !== null
    && input.management_fee_annual !== "")
    ? n(input.management_fee_annual)
    : annualGrossIncome * (Math.min(Math.max(n(input.management_fee_pct), 0), 100) / 100);
  const maintenanceReserve = (input.maintenance_reserve_annual !== undefined
    && input.maintenance_reserve_annual !== null
    && input.maintenance_reserve_annual !== "")
    ? n(input.maintenance_reserve_annual)
    : purchasePrice * (Math.min(Math.max(n(input.maintenance_reserve_pct), 0), 100) / 100);
  const vacancyReserve = annualGrossIncome * (vacancyPct / 100);
  const utilitiesAnnual = isStr ? n(input.utilities_annual) : 0;
  const cleaningAnnual = isStr ? n(input.cleaning_monthly) * 12 : 0;

  const annualOpex =
    serviceCharges +
    insurance +
    managementFee +
    maintenanceReserve +
    vacancyReserve +
    utilitiesAnnual +
    cleaningAnnual;

  // ----- Income statement -----------------------------------------------
  const noi = annualGrossIncome - annualOpex;
  // NOTE: principal repayment (annualPrincipal) is NOT subtracted from
  // cashflow in the German convention — it's wealth transfer, not an
  // expense. But monthly *out-of-pocket* cashflow IS net of full debt
  // service since the bank withdraws the full installment. So we show:
  //   - "Net Cashflow (after Tilgung)" for liquidity planning
  //   - "Effective Yield" excludes Tilgung
  const annualCashflow = noi - annualDebtService;
  const monthlyCashflow = annualCashflow / 12;

  // ----- Dubai / North Cyprus Excel-parity cost lines (Feb 2026) -------
  // These apply when the location uses the Excel-style line-item breakdown.
  // For other locations all values default to 0 and have no effect.
  const loc = (input.location || "").toLowerCase();
  const isDubai = loc === "dubai";
  const isCyprus = loc === "north_cyprus";
  const usesExcelExtras = isDubai || isCyprus;

  const furnitureCost      = usesExcelExtras ? n(input.furniture_cost) : 0;
  const chequeFee          = isDubai ? n(input.cheque_fee) : 0;  // Dubai-only
  const financingSetupCost = usesExcelExtras ? n(input.financing_setup_costs) : 0;
  // Anschaffungsnebenkosten (Excel "base" before geldtransfer + cheque):
  //   closingCosts + renovationCosts + furniture + financingSetup
  const dubaiAnschaffungsBase = usesExcelExtras
    ? (closingCosts + renovationCosts + furnitureCost + financingSetupCost)
    : 0;
  // Wire transfer (Geldtransfer): strictly user-entered. Empty/null/undefined
  // → 0. There is no rate-based auto-calc (user decision Feb 28, 2026 —
  // actual bank charges vary too widely for a rule of thumb to be useful).
  const wireTransferOverride = usesExcelExtras
    && input.wire_transfer_fee !== undefined
    && input.wire_transfer_fee !== null
    && input.wire_transfer_fee !== ""
    ? Number(input.wire_transfer_fee)
    : null;
  const geldtransferCost = wireTransferOverride !== null ? wireTransferOverride : 0;
  // Extra costs that sit ON TOP of the user-typed closing/reno totals.
  const dubaiExtraCosts = usesExcelExtras
    ? (furnitureCost + financingSetupCost + geldtransferCost + chequeFee)
    : 0;
  const effectiveTotalProjectCost = totalProjectCost + dubaiExtraCosts;

  // ----- Yields (Feb 2026 — Excel-aligned German real-estate convention) -
  const safe = (num, den) => (den > 0 ? num / den : 0);
  // Bruttorendite (Gross ROI): pure gross-rent yield over the PURCHASE PRICE
  // only. Matches both the user's reference Dubai Excel and the standard
  // German real-estate convention. It deliberately ignores running costs
  // AND acquisition costs so it's comparable across listings.
  const grossRoiPct = safe(annualGrossIncome, purchasePrice) * 100;
  const grossYieldOnPurchasePct = grossRoiPct; // alias, same definition
  // Nettorendite (Net Yield): NOI / capital actually tied up. For Dubai AND
  // North Cyprus we follow the Excel and EXCLUDE furniture from the
  // denominator (long-term tenants usually bring their own, so furniture
  // doesn't earn rent).
  const netYieldDenominator = usesExcelExtras
    ? Math.max(effectiveTotalProjectCost - furnitureCost, 1)
    : totalProjectCost;
  const netYieldPct = safe(noi, netYieldDenominator) * 100;
  // Equity Yield (levered) = Eigenkapitalrendite
  //   numerator = NOI − interest (NOT − full debt service; Tilgung is wealth)
  //   denominator = equity
  const equityYieldNumerator = noi - annualInterest;
  const equityYieldPct = useFinancing && equity > 0
    ? (equityYieldNumerator / equity) * 100
    : null;

  // ----- Debt coverage --------------------------------------------------
  const dscr = annualDebtService > 0 ? noi / annualDebtService : null;

  // ----- STR break-even -------------------------------------------------
  let breakEvenOccupancyPct = null;
  if (isStr && nightlyRate > 0) {
    // Management fee is now an absolute annual figure — count it as a fixed cost
    // alongside service charges, insurance, etc. Net-per-night then only
    // discounts for vacancy reserve (revenue that won't materialise).
    const fixedAnnualCosts =
      serviceCharges + insurance + managementFee + maintenanceReserve
      + utilitiesAnnual + cleaningAnnual + annualDebtService;
    const netPerNight = nightlyRate * (1 - vacancyPct / 100);
    if (netPerNight > 0) {
      const breakEvenNights = fixedAnnualCosts / netPerNight;
      breakEvenOccupancyPct = (breakEvenNights / 365) * 100;
    }
  }

  return {
    isStr,
    purchasePrice,
    closingCosts,
    renovationCosts,
    totalProjectCost,
    loanAmount,
    downPayment,
    cashInvested,
    equity,

    monthlyDebtService,
    annualDebtService,
    annualInterest,
    annualPrincipal,
    monthlyInterest,
    monthlyPrincipal,

    annualGrossIncome,
    monthlyGrossIncome: annualGrossIncome / 12,
    occupiedNightsPerYear,

    serviceCharges,
    insurance,
    managementFee,
    maintenanceReserve,
    vacancyReserve,
    vacancyPct,
    utilitiesAnnual,
    cleaningAnnual,
    annualOpex,
    monthlyOpex: annualOpex / 12,

    // Dubai-specific Excel-parity lines (zero for non-Dubai deals)
    furnitureCost,
    chequeFee,
    financingSetupCost,
    geldtransferCost,
    dubaiAnschaffungsBase,
    dubaiExtraCosts,
    effectiveTotalProjectCost,

    // Germany Hausgeld breakdown (zero for non-Germany deals)
    hausgeldUmlageAnnual,
    hausgeldNichtUmlageAnnual,
    ruecklageAnnual,

    noi,
    annualCashflow,
    monthlyCashflow,

    // German banking convention yields (canonical names) plus the legacy
    // field names so older ResultsPanel code still finds the same data:
    netYieldPct,
    equityYieldPct,
    netRoiPct: netYieldPct,            // alias for ResultsPanel "Net ROI" slot
    cashOnCashPct: equityYieldPct ?? 0, // alias for ResultsPanel "Cash-on-Cash" slot

    grossRoiPct,
    grossYieldOnPurchasePct,
    dscr,
    breakEvenOccupancyPct,
  };
}

/**
 * Sanity checks. Returns an array of warning strings.
 */
export function validateDeal(input, result) {
  const warnings = [];

  if (n(input.purchase_price) <= 0) warnings.push("Purchase price must be greater than zero.");
  if (n(input.closing_costs) < 0) warnings.push("Closing costs cannot be negative.");
  if (n(input.renovation_costs) < 0) warnings.push("Renovation costs cannot be negative.");

  if (input.use_financing) {
    // Loan-first model: validate loan_amount if present, else fall back to legacy equity validation.
    const hasLoanInput = input.loan_amount !== undefined && input.loan_amount !== null && input.loan_amount !== "";
    if (hasLoanInput) {
      if (n(input.loan_amount) < 0) warnings.push("Loan amount cannot be negative.");
      if (n(input.loan_amount) > result.totalProjectCost && result.totalProjectCost > 0)
        warnings.push("Loan amount exceeds total project cost — capped at 100% LTV.");
    } else {
      if (n(input.equity) < 0) warnings.push("Equity cannot be negative.");
      if (n(input.equity) > result.totalProjectCost && result.totalProjectCost > 0)
        warnings.push("Equity exceeds total project cost — no loan needed.");
    }
    if (n(input.interest_rate_pct) > 15) warnings.push("Interest rate above 15% looks unrealistic — double-check.");
    if (n(input.interest_rate_pct) <= 0 && result.loanAmount > 0)
      warnings.push("Loan has zero interest rate — verify if intentional.");
    if (input.amortization !== "interest_only") {
      if (n(input.tilgungsrate_pct) > 0 && n(input.tilgungsrate_pct) < 1)
        warnings.push("Tilgung below 1% p.a. — loan will take an extremely long time to repay.");
      if (n(input.tilgungsrate_pct) > 10)
        warnings.push("Tilgung above 10% p.a. — very aggressive, check your liquidity.");
    }
  }

  if (input.income_mode === "short_term") {
    if (n(input.occupancy_pct) > 90) warnings.push("Occupancy above 90% rarely sustained year-round — check seasonality.");
    if (n(input.occupancy_pct) < 30 && n(input.nightly_rate) > 0)
      warnings.push("Low occupancy: STR business case typically needs >40% to compete with long-term let.");
  } else {
    const grossYield = result.grossYieldOnPurchasePct;
    if (grossYield > 15) warnings.push("Gross yield >15% — verify rent assumption (yields this high are uncommon).");
    if (grossYield > 0 && grossYield < 2)
      warnings.push("Gross yield <2% — capital appreciation play, not a cashflow asset.");
  }

  if (result.monthlyCashflow < 0)
    warnings.push("Negative monthly cashflow — the property does not cover its costs from rent alone.");
  if (result.equityYieldPct !== null && result.equityYieldPct > 30)
    warnings.push("Equity yield above 30% — leverage is extreme; risk scales with it.");
  if (result.dscr !== null && result.dscr < 1)
    warnings.push("DSCR below 1.0 — the property does not cover its own debt service.");

  return warnings;
}
